<template>
  <div class="modern-form-container">
    <!-- Selector de Plantillas (con animación de entrada/salida) -->
    <transition name="slide-down">
      <div v-if="showTemplateSelector" class="template-wrapper">
        <grid-template-selector
:patient-id="Number(patientId)" :current-grid-data="currentGridData"
          @template-applied="onTemplateApplied" />
        <button class="btn-close-template" title="Ocultar plantillas" @click="showTemplateSelector = false">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </transition>

    <div class="form-header">
      <div class="header-content">
        <div class="title-section">
          <div class="icon-wrapper">
            <i class="fas fa-table"></i>
          </div>
          <div>
            <h4 class="form-title">Datos del Paciente</h4>
            <p class="form-subtitle">Vista tabular editable de la información del paciente</p>
          </div>
        </div>
        <div class="action-buttons">
          <button
class="btn-header btn-template" title="Gestionar plantillas"
            :class="{ 'active': showTemplateSelector }" @click="showTemplateSelector = !showTemplateSelector">
            <i class="fas fa-layer-group"></i>
            <span>Plantillas</span>
          </button>
          <button class="btn-header btn-add" title="Agregar nueva fila" @click="addNewRow">
            <i class="fas fa-plus"></i>
            <span>Agregar Fila</span>
          </button>
          <button class="btn-header btn-columns" title="Gestionar columnas" @click="openColumnManager">
            <i class="fas fa-columns"></i>
            <span>Columnas</span>
          </button>
          <button class="btn-header btn-export" title="Exportar a CSV" @click="exportToCSV">
            <i class="fas fa-file-csv"></i>
            <span>Exportar</span>
          </button>
          <button
class="btn-header btn-save" title="Guardar todos los cambios" :disabled="!hasUnsavedChanges"
            @click="saveChanges()">
            <i class="fas fa-save"></i>
            <span>Guardar</span>
          </button>
        </div>
      </div>
    </div>

    <div class="patient-data-grid-container">
      <div class="grid-controls">
        <div class="control-group">
          <label for="grid-filter" class="control-label">
            <i class="fas fa-filter me-2"></i>Filtrar:
          </label>
          <input
id="grid-filter" v-model="quickFilterText" type="text" placeholder="Buscar en todos los campos..."
            class="form-control" />
        </div>
        <div class="control-info">
          <span v-if="hasUnsavedChanges" class="info-badge">
            <i class="fas fa-circle text-warning me-1"></i>
            Cambios sin guardar
          </span>
        </div>
      </div>

      <div class="ag-grid-wrapper">
        <ag-grid-vue
ref="agGrid" class="ag-theme-alpine patient-grid" :column-defs="columnDefs" :row-data="rowData"
          :default-col-def="defaultColDef" :get-row-id="getRowId" :pagination="true" :pagination-page-size="100"
          :pagination-page-size-selector="[10, 20, 50, 100]" :quick-filter-text="quickFilterText"
          :enable-cell-text-selection="true" :suppress-row-click-selection="false" row-selection="multiple"
          :stop-editing-when-cells-lose-focus="true" @grid-ready="onGridReady"
          @cell-value-changed="onCellValueChanged" />
      </div>

      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
    </div>

    <!-- Modal para gestionar columnas -->
    <base-modal
v-model="showColumnManager" title="Gestionar Columnas" icon="fas fa-columns"
      confirm-text="Agregar Nueva Columna" confirm-icon="fas fa-plus-circle" size="large" @confirm="addNewColumn"
      @close="handleColumnUpdate">
      <div class="columns-manager-intro">
        <i class="fas fa-info-circle"></i>
        <span>Puedes agregar, editar o eliminar columnas de la tabla</span>
      </div>

      <div class="column-list-improved">
        <div v-for="(col, index) in editableColumns" :key="col.field" class="column-item-improved">
          <div class="column-drag">
            <i class="fas fa-grip-vertical"></i>
          </div>
          <div class="column-content">
            <input
v-model="col.headerName" type="text" class="column-name-input-improved"
              placeholder="Nombre de la columna" @change="updateColumnName(col)" />
            <span class="column-field-tag">{{ col.field }}</span>
          </div>
          <div class="column-actions-improved">
            <button
class="btn-delete-column" title="Eliminar columna" :disabled="editableColumns.length <= 1"
              @click="deleteColumn(index)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </base-modal>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Swal from 'sweetalert2';
import api from '@/services/api';
import GridTemplateSelector from './GridTemplateSelector.vue';
import BaseModal from '@/components/BaseModal.vue';

const props = defineProps({
  patientId: {
    type: [String, Number],
    required: true
  }
});

// Referencias
const agGrid = ref(null);
const gridApi = ref(null);
const loading = ref(false);
const quickFilterText = ref('');
const showColumnManager = ref(false);
const showTemplateSelector = ref(false);
const hasUnsavedChanges = ref(false);
const editableColumns = ref([]);

// Computed para pasar grid data actual al selector de plantillas
const currentGridData = computed(() => ({
  columns: columnDefs.value.filter(col => col.field !== 'actions' && col.field !== 'rowNumber'),
  rows: rowData.value
}));

// Configuración por defecto de columnas - TODAS EDITABLES
const defaultColDef = ref({
  flex: 1,
  minWidth: 100,
  resizable: true,
  sortable: true,
  filter: true,
  editable: true, // Hacer todas las celdas editables
  cellEditor: 'agTextCellEditor',
});

// Definición de columnas inicial
const columnDefs = ref([
  {
    headerName: '#',
    field: 'rowNumber',
    pinned: 'left',
    width: 70,
    editable: false,
    cellStyle: { fontWeight: 'bold', textAlign: 'center' },
    valueGetter: (params) => {
      return params.node.rowIndex + 1;
    }
  },
  {
    headerName: 'Categoría',
    field: 'category',
    pinned: 'left',
    width: 150,
    editable: true,
    cellStyle: { fontWeight: 'bold' }
  },
  {
    headerName: 'Campo',
    field: 'field',
    width: 200,
    editable: true,
  },
  {
    headerName: 'Valor',
    field: 'value',
    width: 250,
    editable: true,
    cellRenderer: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') {
        return '<span style="color: #999; font-style: italic;">Doble clic para editar</span>';
      }
      return params.value;
    }
  },
  {
    headerName: 'Tipo',
    field: 'type',
    width: 120,
    editable: true,
  },
  {
    headerName: 'Acciones',
    field: 'actions',
    width: 100,
    pinned: 'right',
    editable: false,
    cellStyle: { textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cellRenderer: (params) => {
      return `
        <button class="btn-delete-row" data-row-index="${params.node.rowIndex}" title="Eliminar fila">
          <i class="fas fa-trash"></i>
        </button>
      `;
    }
  }
]);

// Datos de ejemplo
const rowData = ref([]);

// Función para identificar filas por ID
const getRowId = (params) => {
  return params.data.id.toString();
};

// Función que se ejecuta cuando el grid está listo
const onGridReady = (params) => {
  gridApi.value = params.api;
  loadPatientData();
  syncEditableColumns();
};

// Cargar datos del paciente
const loadPatientData = async () => {
  if (!props.patientId) {
    return;
  }

  loading.value = true;
  try {
    const response = await api.get(`/pacientes/${props.patientId}/grid-data`);

    if (response.data.success && response.data.grid_data) {
      const gridData = response.data.grid_data;

      if (gridData.columns && Array.isArray(gridData.columns)) {
        const specialColumns = columnDefs.value.filter(col =>
          col.field === 'rowNumber' || col.field === 'actions'
        );
        const serverColumns = gridData.columns.map(col => ({
          ...col,
          headerName: col.headerName || col.field,
          field: col.field,
          width: col.width || 150,
          editable: col.editable !== undefined ? col.editable : true,
          sortable: true,
          filter: true,
          resizable: true
        }));
        columnDefs.value = [...specialColumns.slice(0, 1), ...serverColumns, ...specialColumns.slice(1)];
      }

      // Cargar filas si existen
      if (gridData.rows && Array.isArray(gridData.rows)) {
        rowData.value = gridData.rows.map(row => ({
          ...row
        }));
      } else {
        rowData.value = [];
      }

      // Ajustar columnas al tamaño del grid
      if (gridApi.value) {
        gridApi.value.sizeColumnsToFit();
      }

      hasUnsavedChanges.value = false;
    } else {
      rowData.value = [];
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.log('🆕 Paciente sin datos guardados, iniciando con grid vacío');
      rowData.value = [];
    } else {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Error al cargar los datos del paciente',
        icon: 'error',
        confirmButtonText: 'Entendido',
        customClass: {
          confirmButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
      });
    }
  } finally {
    loading.value = false;
  }
};

// Detectar cambios en las celdas
const onCellValueChanged = async () => {
  hasUnsavedChanges.value = true;

  await saveChanges(true);
};

// Agregar nueva fila
const addNewRow = async () => {
  const newId = Math.max(...rowData.value.map(r => r.id || 0), 0) + 1;
  const newRow = {
    id: newId,
    category: '',
    field: '',
    value: '',
    type: ''
  };

  // Agregar al array reactivo
  rowData.value.push(newRow);
  hasUnsavedChanges.value = true;

  if (gridApi.value) {
    // Usar applyTransaction para agregar la fila de manera eficiente
    gridApi.value.applyTransaction({ add: [newRow] });

    // Scroll a la última fila y enfocar
    setTimeout(() => {
      const lastRowIndex = rowData.value.length - 1;
      gridApi.value.ensureIndexVisible(lastRowIndex, 'bottom');

      // Enfocar la primera celda editable de la nueva fila
      const firstEditableColumn = columnDefs.value.find(col => col.editable && col.field !== 'rowNumber');
      if (firstEditableColumn) {
        gridApi.value.setFocusedCell(lastRowIndex, firstEditableColumn.field);
        gridApi.value.startEditingCell({
          rowIndex: lastRowIndex,
          colKey: firstEditableColumn.field
        });
      }
    }, 100);
  }

  await saveChanges(true);
};

// Eliminar fila con SweetAlert2
const deleteRow = async (rowIndex) => {
  if (!gridApi.value) return;

  // Obtener la fila actual por su índice visual
  const rowNode = gridApi.value.getDisplayedRowAtIndex(rowIndex);
  if (!rowNode || !rowNode.data) {
    console.error('No se pudo encontrar la fila');
    return;
  }

  if (rowData.value.length <= 1) {
    await Swal.fire({
      title: 'No se puede eliminar',
      text: 'Debe permanecer al menos una fila en la tabla.',
      icon: 'warning',
      confirmButtonText: 'Entendido',
      customClass: {
        confirmButton: 'btn bg-gradient-primary'
      },
      buttonsStyling: false
    });
    return;
  }

  const rowToDelete = rowNode.data;

  const result = await Swal.fire({
    title: '¿Estás seguro?',
    html: '¡No podrás revertir esto!<br>¿Deseas eliminar esta fila?',
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: 'Cancelar',
    confirmButtonText: 'Sí, elimínala',
    customClass: {
      confirmButton: 'btn bg-gradient-success',
      cancelButton: 'btn bg-gradient-danger'
    },
    buttonsStyling: false
  });

  if (result.isConfirmed) {
    // Eliminar del array reactivo usando el ID
    const arrayIndex = rowData.value.findIndex(row => row.id === rowToDelete.id);
    if (arrayIndex !== -1) {
      rowData.value.splice(arrayIndex, 1);
    }

    hasUnsavedChanges.value = true;
    gridApi.value.applyTransaction({ remove: [rowToDelete] });

    await Swal.fire({
      title: '¡Eliminada!',
      text: 'La fila ha sido eliminada.',
      icon: 'success',
      showConfirmButton: false,
      timer: 1500
    });

    // Autoguardar en modo silencioso después de eliminar
    console.log('💾 Autoguardando después de eliminar...');
    await saveChanges(true);
  }
};

// Sincronizar columnas editables (excluyendo la columna de acciones y numeración)
const syncEditableColumns = () => {
  editableColumns.value = columnDefs.value
    .filter(col => col.field !== 'actions' && col.field !== 'rowNumber')
    .map(col => ({ ...col }));
};

// Abrir modal de columnas (sincroniza antes de abrir)
const openColumnManager = () => {
  syncEditableColumns();
  showColumnManager.value = true;
};

// Actualizar nombre de columna
const updateColumnName = (col) => {
  const columnIndex = columnDefs.value.findIndex(c => c.field === col.field);
  if (columnIndex !== -1) {
    columnDefs.value[columnIndex].headerName = col.headerName;
    if (gridApi.value) {
      gridApi.value.setGridOption('columnDefs', columnDefs.value);
    }
    hasUnsavedChanges.value = true;
  }
};

// Eliminar columna con SweetAlert2
const deleteColumn = async (index) => {
  if (editableColumns.value.length <= 1) {
    await Swal.fire({
      title: 'No se puede eliminar',
      text: 'Debe haber al menos una columna',
      icon: 'warning',
      confirmButtonText: 'Entendido',
      customClass: {
        confirmButton: 'btn bg-gradient-primary'
      },
      buttonsStyling: false
    });
    return;
  }

  const result = await Swal.fire({
    title: '¿Estás seguro?',
    html: '¡No podrás revertir esto!<br>¿Deseas eliminar esta columna?',
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: 'Cancelar',
    confirmButtonText: 'Sí, elimínala',
    customClass: {
      confirmButton: 'btn bg-gradient-success',
      cancelButton: 'btn bg-gradient-danger'
    },
    buttonsStyling: false
  });

  if (result.isConfirmed) {
    const fieldToDelete = editableColumns.value[index].field;

    // Eliminar de columnDefs
    columnDefs.value = columnDefs.value.filter(col => col.field !== fieldToDelete);

    // Eliminar de editableColumns
    editableColumns.value.splice(index, 1);

    // Eliminar datos de esa columna de todas las filas
    rowData.value.forEach(row => {
      delete row[fieldToDelete];
    });

    if (gridApi.value) {
      gridApi.value.setGridOption('columnDefs', columnDefs.value);
      gridApi.value.setGridOption('rowData', rowData.value);
    }

    hasUnsavedChanges.value = true;

    await Swal.fire({
      title: '¡Eliminada!',
      text: 'La columna ha sido eliminada.',
      icon: 'success',
      showConfirmButton: false,
      timer: 1500
    });
  }
};

// Manejar actualización de columnas desde el modal
const handleColumnUpdate = () => {
  showColumnManager.value = false;
};

// Agregar nueva columna
const addNewColumn = () => {
  const newColumnNumber = columnDefs.value.length;
  const newField = `customField${newColumnNumber}`;

  const newColumn = {
    headerName: `Nueva Columna ${newColumnNumber}`,
    field: newField,
    width: 150,
    editable: true,
  };

  // Agregar antes de la columna de acciones
  const actionsIndex = columnDefs.value.findIndex(col => col.field === 'actions');
  if (actionsIndex !== -1) {
    columnDefs.value.splice(actionsIndex, 0, newColumn);
  } else {
    columnDefs.value.push(newColumn);
  }

  // Agregar campo vacío a todas las filas
  rowData.value.forEach(row => {
    row[newField] = '';
  });

  syncEditableColumns();

  if (gridApi.value) {
    gridApi.value.setGridOption('columnDefs', columnDefs.value);
    gridApi.value.setGridOption('rowData', rowData.value);
  }

  hasUnsavedChanges.value = true;
};

// Exportar a CSV
const exportToCSV = () => {
  if (gridApi.value) {
    gridApi.value.exportDataAsCsv({
      fileName: `datos-paciente-${props.patientId}.csv`,
      columnKeys: columnDefs.value.filter(col => col.field !== 'actions' && col.field !== 'rowNumber').map(col => col.field)
    });
  }
};

// Guardar cambios
const saveChanges = async (silent = false) => {
  if (!props.patientId) {
    await Swal.fire({
      title: 'Error',
      text: 'No hay paciente seleccionado',
      icon: 'error',
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#ef4444'
    });
    return;
  }

  try {
    loading.value = true;

    // Preparar columnas (sin las especiales)
    const columnsToSave = columnDefs.value
      .filter(col => col.field !== 'actions' && col.field !== 'rowNumber')
      .map(col => ({
        headerName: col.headerName,
        field: col.field,
        width: col.width,
        editable: col.editable
      }));

    // Preparar filas a partir del estado reactivo (asegura que eliminaciones/ediciones se persistan)
    const rowsToSave = rowData.value
      .map(row => ({ ...row }))
      .filter(row => {
        // Filtrar filas que solo tengan null, undefined o id
        const keys = Object.keys(row).filter(k => k !== 'id' && k !== '__ob__' && k !== '_observers');
        const hasData = keys.some(key => row[key] != null && row[key] !== '');
        return hasData || keys.length > 0;
      })
      .map(row => {
        const cleanRow = { ...row };
        // Eliminar propiedades internas de AG Grid si existen
        delete cleanRow.__ob__;
        delete cleanRow._observers;

        return cleanRow;
      });
    const payload = {
      grid_data: {
        columns: columnsToSave,
        rows: rowsToSave
      }
    };

    // Enviar datos al servidor
    await api.put(`/pacientes/${props.patientId}/grid-data`, payload);

    rowData.value = [...rowsToSave];
    hasUnsavedChanges.value = false;

    if (!silent) {
      await Swal.fire({
        title: '¡Guardado exitoso!',
        text: rowsToSave.length === 0
          ? 'Todos los datos han sido eliminados correctamente'
          : `${rowsToSave.length} fila(s) guardada(s) correctamente`,
        icon: 'success',
        confirmButtonText: 'Perfecto',
        confirmButtonColor: '#10b981',
        timer: 3000,
        timerProgressBar: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown animate__faster'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp animate__faster'
        }
      });
    }
  } catch (error) {
    console.error('❌ Error al guardar:', error);
    console.error('📍 Detalles completos del error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText
    });

    // Siempre mostrar error
    if (!silent) {
      await Swal.fire({
        title: 'Error al guardar',
        text: error.response?.data?.message || 'Hubo un problema al guardar los cambios. Inténtalo de nuevo.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444'
      });
    }
  } finally {
    loading.value = false;
  }
};

// Actualizar datos (comentada temporalmente)
// const refreshData = () => {
//   if (hasUnsavedChanges.value) {
//     if (confirm('Tienes cambios sin guardar. ¿Quieres recargar y perder los cambios?')) {
//       hasUnsavedChanges.value = false;
//       loadPatientData();
//     }
//   } else {
//     loadPatientData();
//   }
// };

// Watch para cambios en el patientId
watch(() => props.patientId, (newVal) => {
  if (newVal && gridApi.value) {
    loadPatientData();
  }
});

// Handlers para el selector de plantillas
const onTemplateApplied = async (appliedData) => {
  await loadPatientData();

  await Swal.fire({
    icon: 'success',
    title: 'Plantilla Aplicada',
    text: `La plantilla "${appliedData.template_name}" se aplicó correctamente`,
    timer: 2000,
    showConfirmButton: false
  });
};

// Manejar clicks en los botones de eliminar usando delegación de eventos
const handleGridClick = (e) => {
  if (e.target.closest('.btn-delete-row')) {
    const button = e.target.closest('.btn-delete-row');
    const rowIndex = parseInt(button.getAttribute('data-row-index'));
    deleteRow(rowIndex);
  }
};

onMounted(() => {
  // Agregar event listener global para los botones de eliminar
  const gridContainer = document.querySelector('.patient-data-grid-container');
  if (gridContainer) {
    gridContainer.addEventListener('click', handleGridClick);
  }
});
</script>

<style scoped>
.modern-form-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 0;
  overflow: hidden;
}

/* Template Selector Wrapper */
.template-wrapper {
  position: relative;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-bottom: 2px solid #cbd5e1;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.btn-close-template {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.btn-close-template:hover {
  background: white;
  color: #ef4444;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Animaciones de slide */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.form-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #cbd5e1;
  padding: 0.875rem 1.5rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;
}

.icon-wrapper {
  width: 42px;
  height: 42px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
}

.form-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 0.125rem 0 0 0;
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 400;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.btn-header {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-header i {
  font-size: 0.875rem;
}

.btn-header:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

.btn-header:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.btn-header:not(:disabled):active {
  transform: translateY(0);
}

.btn-template {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  position: relative;
}

.btn-template.active {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.btn-template.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: #6366f1;
  border-radius: 2px;
}

.btn-add {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.btn-columns {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
}

.btn-export {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
}

.btn-save {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
}

.unsaved-badge {
  background: #f59e0b;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  animation: pulse 2s infinite;
  margin-right: 0.5rem;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

.patient-data-grid-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  /* Permitir scroll cuando sea necesario */
  width: 100%;
}

.grid-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 1rem;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex: 1;
  max-width: 500px;
}

.control-label {
  margin: 0;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  font-size: 0.8125rem;
}

.form-control {
  padding: 0.5rem 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  flex: 1;
  background: #f8fafc;
}

.form-control:hover {
  border-color: #94a3b8;
  background: white;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.control-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #fbbf24;
  animation: pulse 2s infinite;
}

.info-badge i {
  color: #f59e0b;
}

.toolbar-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.5rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: white;
  color: #495057;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.btn i {
  font-size: 0.875rem;
}

.ag-grid-wrapper {
  flex: 1;
  overflow: auto;
  /* Permitir scroll horizontal y vertical */
  padding: 1.5rem 2rem;
  background: #fff;
}

.patient-grid {
  height: 100%;
  width: 100%;
  min-width: 100%;
  /* Asegurar que el grid se expanda con las columnas */
  font-size: 0.875rem;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Modal de gestión de columnas */
.column-manager-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #141727;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f8f9fa;
  color: #141727;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* Lista de columnas */
.columns-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.column-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.column-item:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.column-item input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.column-item input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.column-actions {
  display: flex;
  gap: 0.25rem;
}

.btn-icon {
  background: none;
  border: none;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #6c757d;
}

.btn-icon:hover {
  background: #dee2e6;
  color: #141727;
}

.btn-icon.danger:hover {
  background: #dc3545;
  color: white;
}

/* Botón eliminar en grid */
:deep(.btn-delete-row) {
  background: #fff;
  border: 1px solid #dc3545;
  color: #dc3545;
  cursor: pointer;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}

:deep(.btn-delete-row:hover) {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
  transform: scale(1.05);
}

:deep(.btn-delete-row:active) {
  transform: scale(0.95);
}

:deep(.btn-delete-row i) {
  pointer-events: none;
}

/* Estilos personalizados para AG Grid */
:deep(.ag-theme-alpine) {
  --ag-header-background-color: #f8f9fa;
  --ag-header-foreground-color: #495057;
  --ag-odd-row-background-color: #ffffff;
  --ag-row-hover-color: #f1f3f5;
  --ag-selected-row-background-color: #e7f5ff;
  --ag-border-color: #e9ecef;
}

:deep(.ag-header-cell-label) {
  font-weight: 600;
}

:deep(.ag-cell) {
  display: flex;
  align-items: center;
}

:deep(.ag-root-wrapper) {
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

/* Responsive */
@media (max-width: 768px) {
  .form-header {
    padding: 1rem 1.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons {
    width: 100%;
    justify-content: stretch;
  }

  .header-btn-secondary,
  .btn-success,
  .btn-primary,
  .btn-secondary {
    flex: 1;
    justify-content: center;
  }

  .grid-controls {
    padding: 1rem 1.5rem;
    flex-direction: column;
    align-items: stretch;
  }

  .control-group {
    max-width: 100%;
  }

  .toolbar-buttons {
    width: 100%;
  }

  .ag-grid-wrapper {
    padding: 1rem 1.5rem;
  }
}

/* ========================================
   CONTENIDO ESPECÍFICO DEL MODAL DE COLUMNAS
======================================== */
.columns-manager-intro {
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1e40af;
  font-size: 0.9375rem;
}

.columns-manager-intro i {
  font-size: 1.25rem;
}

.column-list-improved {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.column-item-improved {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: #fafbfc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.column-item-improved:hover {
  background: white;
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.column-drag {
  color: #94a3b8;
  cursor: grab;
  font-size: 1.125rem;
  transition: color 0.2s;
}

.column-drag:active {
  cursor: grabbing;
}

.column-drag:hover {
  color: #64748b;
}

.column-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.column-name-input-improved {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
}

.column-name-input-improved:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.column-field-tag {
  font-size: 0.75rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  align-self: flex-start;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-weight: 500;
}

.column-actions-improved {
  display: flex;
  gap: 0.5rem;
}

.btn-delete-column {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #fecaca;
  border-radius: 10px;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
}

.btn-delete-column:hover:not(:disabled) {
  background: #ef4444;
  border-color: #ef4444;
  color: white;
  transform: scale(1.05);
}

.btn-delete-column:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-color: #e2e8f0;
  color: #cbd5e1;
}

/* Responsive: Estilos específicos de columnas */
@media (max-width: 1024px) {
  .btn-header span {
    display: none;
  }

  .btn-header {
    padding: 0.5rem;
    min-width: 38px;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .form-header {
    padding: 0.75rem 1rem;
  }

  .icon-wrapper {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }

  .form-title {
    font-size: 1rem;
  }

  .form-subtitle {
    font-size: 0.75rem;
  }

  .action-buttons {
    width: 100%;
  }

  .grid-controls {
    padding: 0.75rem 1rem;
  }

  .column-item-improved {
    flex-wrap: wrap;
    padding: 1rem;
  }

  .column-content {
    min-width: 100%;
  }
}
</style>
